/**
 * @fileoverview Institution Management page for Eurofarma Comercial and Gestão Lena
 * @description Allows viewing, editing, and managing all institutions
 */
import React, { useState, useCallback, useRef } from "react";
import {
  DmsPage,
  DmsTabber,
  DmsAltBox,
  DmsTable,
  DmsButton,
  DmsButtonGroup,
  DmsPermissions,
  intl,
  fetchData,
  HTTP_STATUS,
  Notifications,
} from "dmsbasereact";
import { Typography, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Box, Chip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Permissions } from "../../constants/Permissions";
import { InstitutionSetupModal } from "../../components/InstitutionSetupModal";
import { InstitutionDetailModal } from "./InstitutionDetailModal";
import { getInstitutionCategoryLabel, InstitutionCategoryEnum } from "../../constants/InstitutionType";

/**
 * Get status color based on status text
 */
const getStatusColor = (status) => {
  if (!status) return "default";
  const statusLower = status.toLowerCase();
  // Check "Pendente" first (before "aprovação" matches)
  if (statusLower.includes("pend")) return "warning";
  // Check inactive before active
  if (statusLower.includes("inativ") || statusLower.includes("inactive")) return "default";
  // Active/Approved
  if (statusLower.includes("ativ") || statusLower.includes("active")) return "success";
  if (statusLower.includes("aprov") || statusLower.includes("approved")) return "success";
  // Rejected
  if (statusLower.includes("rejeit") || statusLower.includes("rejected")) return "error";
  // Awaiting
  if (statusLower.includes("aguard") || statusLower.includes("await")) return "info";
  return "default";
};

/**
 * Institution Management page component
 */
export const InstitutionManagementPage = () => {
  const tableAllRef = useRef(null);
  const tableDistributorRef = useRef(null);
  const tableBuyerRef = useRef(null);
  const tableIntermediaryRef = useRef(null);
  const tablePrescriberRef = useRef(null);
  const tableDispenserRef = useRef(null);

  // View Details Modal (read-only, nice display)
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedInstitutionForView, setSelectedInstitutionForView] = useState(null);

  // Edit/Create Modal (form)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editModalMode, setEditModalMode] = useState("create"); // edit or create
  const [selectedInstitutionForEdit, setSelectedInstitutionForEdit] = useState(null);
  const [loadingInstitution, setLoadingInstitution] = useState(false);

  // Deactivate/Reactivate Modal
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [institutionForAction, setInstitutionForAction] = useState(null);
  const [justification, setJustification] = useState("");
  const [loading, setLoading] = useState(false);

  const updateAllTables = useCallback(() => {
    tableAllRef.current?.updateTable();
    tableDistributorRef.current?.updateTable();
    tableBuyerRef.current?.updateTable();
    tableIntermediaryRef.current?.updateTable();
    tablePrescriberRef.current?.updateTable();
    tableDispenserRef.current?.updateTable();
  }, []);

  // Fetch full institution details for editing
  const fetchInstitutionDetails = useCallback(async (institutionPk) => {
    setLoadingInstitution(true);
    try {
      const response = await fetchData(`/admin/institutions/${institutionPk}/details`, "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        return response.data?.data || null;
      }
      return null;
    } catch (error) {
      console.error("Error fetching institution details:", error);
      return null;
    } finally {
      setLoadingInstitution(false);
    }
  }, []);

  // View Details handler - opens read-only modal with nice display
  const handleViewDetails = useCallback((row) => {
    setSelectedInstitutionForView(row);
    setDetailsModalOpen(true);
  }, []);

  const handleCloseDetailsModal = useCallback(() => {
    setDetailsModalOpen(false);
    setSelectedInstitutionForView(null);
  }, []);

  // Edit handler - fetches full data and opens edit modal
  const handleEditInstitution = useCallback(async (row) => {
    // Close details modal first
    setDetailsModalOpen(false);

    // Fetch full institution data for editing
    const details = await fetchInstitutionDetails(row.pk);
    if (details) {
      setSelectedInstitutionForEdit(details);
      setEditModalMode("edit");
      setEditModalOpen(true);
    }
  }, [fetchInstitutionDetails]);

  // Create Institution handler
  const handleCreateInstitution = useCallback(() => {
    setSelectedInstitutionForEdit(null);
    setEditModalMode("create");
    setEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setSelectedInstitutionForEdit(null);
  }, []);

  // Save institution handler
  const handleSaveInstitution = useCallback(async (payload, institutionPk) => {
    try {
      let response;
      if (institutionPk) {
        response = await fetchData(`/institution/${institutionPk}`, 'PUT', true, payload);
      } else {
        response = await fetchData('/institution', 'POST', true, payload);
      }

      if (response.status === HTTP_STATUS.OK_200 || response.status === HTTP_STATUS.CREATED_201) {
        Notifications.toast(
          intl.get("InstitutionSavedSuccessfully").d("Institution saved successfully"),
          Notifications.TYPE_SUCCESS
        );
        updateAllTables();
        handleCloseEditModal();
        return true;
      } else {
        const errorMessage = response.data?.detail || intl.get("ErrorSavingInstitution").d("Error saving institution");
        Notifications.toast(errorMessage, Notifications.TYPE_ERROR);
        return false;
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.detail ||
        error?.data?.detail ||
        error?.message ||
        intl.get("ErrorSavingInstitution").d("Error saving institution");
      Notifications.toast(errorMessage, Notifications.TYPE_ERROR);
      return false;
    }
  }, [updateAllTables, handleCloseEditModal]);

  // Deactivate handlers
  const handleOpenDeactivateModal = useCallback((row) => {
    setInstitutionForAction(row);
    setJustification("");
    setDeactivateModalOpen(true);
  }, []);

  const handleCloseDeactivateModal = useCallback(() => {
    setDeactivateModalOpen(false);
    setInstitutionForAction(null);
    setJustification("");
  }, []);

  const handleDeactivate = useCallback(async () => {
    if (!justification.trim()) {
      Notifications.toast(
        intl.get("JustificationRequired").d("Justification is required!"),
        Notifications.TYPE_WARNING
      );
      return;
    }

    setLoading(true);
    try {
      const response = await fetchData(
        `/admin/institutions/${institutionForAction.pk}/deactivate`,
        "POST",
        true,
        { justification }
      );

      if (response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(
          intl.get("InstitutionDeactivated").d("Institution deactivated successfully"),
          Notifications.TYPE_SUCCESS
        );
        handleCloseDeactivateModal();
        updateAllTables();
      } else {
        Notifications.toast(
          response.data?.detail || intl.get("ErrorDeactivatingInstitution").d("Error deactivating institution"),
          Notifications.TYPE_ERROR
        );
      }
    } catch (error) {
      Notifications.toast(
        intl.get("ErrorDeactivatingInstitution").d("Error deactivating institution"),
        Notifications.TYPE_ERROR
      );
    } finally {
      setLoading(false);
    }
  }, [institutionForAction, justification, handleCloseDeactivateModal, updateAllTables]);

  // Reactivate handlers
  const handleOpenReactivateModal = useCallback((row) => {
    setInstitutionForAction(row);
    setJustification("");
    setReactivateModalOpen(true);
  }, []);

  const handleCloseReactivateModal = useCallback(() => {
    setReactivateModalOpen(false);
    setInstitutionForAction(null);
    setJustification("");
  }, []);

  const handleReactivate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchData(
        `/admin/institutions/${institutionForAction.pk}/reactivate`,
        "POST",
        true,
        { justification }
      );

      if (response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(
          intl.get("InstitutionReactivated").d("Institution reactivated successfully"),
          Notifications.TYPE_SUCCESS
        );
        handleCloseReactivateModal();
        updateAllTables();
      } else {
        Notifications.toast(
          response.data?.detail || intl.get("ErrorReactivatingInstitution").d("Error reactivating institution"),
          Notifications.TYPE_ERROR
        );
      }
    } catch (error) {
      Notifications.toast(
        intl.get("ErrorReactivatingInstitution").d("Error reactivating institution"),
        Notifications.TYPE_ERROR
      );
    } finally {
      setLoading(false);
    }
  }, [institutionForAction, justification, handleCloseReactivateModal, updateAllTables]);

  const header = [
    {
      label: intl.get("Name").d("Name"),
      column_name: "name",
      filter: true,
    },
    {
      label: intl.get("CNPJ").d("CNPJ"),
      column_name: "cnpj",
      width: "160px",
      filter: true,
    },
    {
      label: intl.get("City").d("City"),
      column_name: "city",
      width: "150px",
    },
    {
      label: intl.get("State").d("State"),
      column_name: "state",
      width: "80px",
    },
    {
      label: intl.get("Category").d("Category"),
      column_name: "category",
      width: "140px",
      element: (row) => {
        if (row.categories && row.categories.length > 0) {
          return row.categories.map(c =>
            <Chip
              size="small"
              variant="outlined"
              label = {getInstitutionCategoryLabel(c)}
           />
          );
        }
        return <Chip
          label = {getInstitutionCategoryLabel(row.category)}
          size="small"
          variant="outlined"
          />
      },
    },
    {
      label: intl.get("Status").d("Status"),
      column_name: "status",
      width: "160px",
      element: (row) => (
        <Chip
          label={row.status || "--"}
          color={getStatusColor(row.status)}
          size="small"
          variant="filled"
        />
      ),
    },
    {
      label: intl.get("Actions").d("Actions"),
      column_name: "actions",
      width: "120px",
      element: (row) => {
        const isInactive = row.status?.toLowerCase() === "inativo" || row.status?.toLowerCase() === "inactive";

        return (
          <DmsButtonGroup>
            <DmsButton
              icon={<VisibilityIcon />}
              title={intl.get("ViewDetails").d("View Details")}
              onClick={() => handleViewDetails(row)}
            />
            <DmsPermissions permissions={Permissions.INSTITUTION_DEACTIVATE}>
              {isInactive ? (
                <DmsButton
                  icon={<LockOpenIcon />}
                  title={intl.get("ReactivateInstitution").d("Reactivate Institution")}
                  onClick={() => handleOpenReactivateModal(row)}
                  color="success"
                />
              ) : (
                <DmsButton
                  icon={<BlockIcon />}
                  title={intl.get("DeactivateInstitution").d("Deactivate Institution")}
                  onClick={() => handleOpenDeactivateModal(row)}
                  color="warning"
                />
              )}
            </DmsPermissions>
          </DmsButtonGroup>
        );
      },
    },
  ];

  const tabs = [
    {
      title: intl.get("AllInstitutions").d("All Institutions"),
      component: (
        <DmsTable
          ref={tableAllRef}
          url="/admin/institutions/table"
          header={header}
          managed={true}
          needs_authentication={true}
          export_table={false}
        />
      ),
    },
    {
      title: intl.get("InstitutionCategory.Distributor").d("Distributors"),
      component: (
        <DmsTable
          ref={tableDistributorRef}
          url="/admin/institutions/table"
          header={header}
          managed={true}
          needs_authentication={true}
          pre_filter={[{ name: "category", value: InstitutionCategoryEnum.Distributor }]}
          export_table={false}
        />
      ),
    },
    {
      title: intl.get("InstitutionCategory.Buyer").d("Buyers"),
      component: (
        <DmsTable
          ref={tableBuyerRef}
          url="/admin/institutions/table"
          header={header}
          managed={true}
          needs_authentication={true}
          pre_filter={[{ name: "category", value: InstitutionCategoryEnum.Buyer }]}
          export_table={false}
        />
      ),
    },
    {
      title: intl.get("InstitutionCategory.Intermediary").d("Intermediaries"),
      component: (
        <DmsTable
          ref={tableIntermediaryRef}
          url="/admin/institutions/table"
          header={header}
          managed={true}
          needs_authentication={true}
          pre_filter={[{ name: "category", value: InstitutionCategoryEnum.Intermediary }]}
          export_table={false}
        />
      ),
    },
    {
      title: intl.get("InstitutionCategory.Prescriber").d("Prescribers"),
      component: (
        <DmsTable
          ref={tablePrescriberRef}
          url="/admin/institutions/table"
          header={header}
          managed={true}
          needs_authentication={true}
          pre_filter={[{ name: "category", value: InstitutionCategoryEnum.Prescriber }]}
          export_table={false}
        />
      ),
    },
    {
      title: intl.get("InstitutionCategory.Dispenser").d("Dispensers"),
      component: (
        <DmsTable
          ref={tableDispenserRef}
          url="/admin/institutions/table"
          header={header}
          managed={true}
          needs_authentication={true}
          pre_filter={[{ name: "category", value: InstitutionCategoryEnum.Dispenser }]}
          export_table={false}
        />
      ),
    },
  ];

  return (
    <DmsPage name={intl.get("InstitutionManagement").d("Institution Management")} manual_slug="institution-management">
      <DmsAltBox.Card>
        <DmsAltBox.Header>
          <Typography variant="h3" sx={{ fontSize: "1.1rem" }}>
            {intl.get("InstitutionManagement").d("Institution Management")}
          </Typography>
        </DmsAltBox.Header>
        <DmsAltBox.Body>
          <DmsTabber tabs={tabs} pageName={intl.get("InstitutionManagement").d("Institution Management")} />
        </DmsAltBox.Body>
      </DmsAltBox.Card>

      {/* View Details Modal (read-only with nice display) */}
      <InstitutionDetailModal
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        institution={selectedInstitutionForView}
        onEdit={handleEditInstitution}
      />

      {/* Edit/Create Modal (form) */}
      <InstitutionSetupModal
        isVisible={editModalOpen}
        onClose={handleCloseEditModal}
        onSave={handleSaveInstitution}
        onRefresh={updateAllTables}
        institution={selectedInstitutionForEdit}
        mode={editModalMode}
      />

      {/* Loading overlay for institution details */}
      {loadingInstitution && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Deactivate Institution Modal */}
      <Dialog open={deactivateModalOpen} onClose={handleCloseDeactivateModal} maxWidth="sm" fullWidth>
        <DialogTitle>{intl.get("DeactivateInstitution").d("Deactivate Institution")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {intl.get("DeactivateInstitutionMessage").d("Please provide a reason for deactivating this institution:")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {institutionForAction?.name}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={intl.get("Justification").d("Justification")}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={handleCloseDeactivateModal}
            disabled={loading}
          />
          <DmsButton
            variant="warning"
            text={loading ? intl.get("Processing").d("Processing...") : intl.get("Deactivate").d("Deactivate")}
            onClick={handleDeactivate}
            disabled={loading || !justification.trim()}
          />
        </DialogActions>
      </Dialog>

      {/* Reactivate Institution Modal */}
      <Dialog open={reactivateModalOpen} onClose={handleCloseReactivateModal} maxWidth="sm" fullWidth>
        <DialogTitle>{intl.get("ReactivateInstitution").d("Reactivate Institution")}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {intl.get("ReactivateInstitutionMessage").d("Are you sure you want to reactivate this institution?")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {institutionForAction?.name}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={intl.get("JustificationOptional").d("Justification (optional)")}
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <DmsButton
            variant="secondary"
            text={intl.get("Cancel").d("Cancel")}
            onClick={handleCloseReactivateModal}
            disabled={loading}
          />
          <DmsButton
            variant="success"
            text={loading ? intl.get("Processing").d("Processing...") : intl.get("Reactivate").d("Reactivate")}
            onClick={handleReactivate}
            disabled={loading}
          />
        </DialogActions>
      </Dialog>
    </DmsPage>
  );
};

export default InstitutionManagementPage;
